/**
 * The Kolor marki tab's wiring: one read, one draft, one dirty flag, one save. It decides nothing —
 * the colours are `entities/brand-color`'s, the verb is `brandColorRequest`'s, the maths are
 * `model/colorPicker`'s.
 *
 * `selection = draft ?? saved`, so the tiles, the picker and the phone follow one value and nothing
 * is committed until Zapisz. The save is OPTIMISTIC because the cache is what paints the colour:
 * writing there first settles the tiles, the phone and the whole app (the shell reads the same
 * entry, see `app/lib/brandPaint.ts`) on one frame, and the rollback is the whole reason — a
 * screen left standing on a colour the server refused is the defect this prevents.
 *
 * The picker holds HSV, not the hex: black, white and every grey have no hue in RGB, so a drag
 * round-tripped through a hex would reset the hue bar at the bottom of the plane.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  deleteMeBrandColor,
  putMeBrandColor,
} from '~/shared/api/generated/endpoints/my-brand-color/my-brand-color';
import type { TrainWithMeModulesClientsDtosResponsesBrandColorResponse as BrandColorResponse } from '~/shared/api/generated/model';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';

import {
  brandColorQueryKey,
  brandColorQueryOptions,
  brandColorRequest,
  brandPaint,
  DEFAULT_BRAND_COLOR,
  customFillAdjustment,
  isBrandHex,
  normaliseHexInput,
  sameBrandColor,
  toBrandColorResponse,
  type BrandColor,
  type BrandPaint,
  type BrandPresetId,
} from '~/entities/brand-color';

import { hexToHsv, hsvFromHex, hsvToHex, type Hsv } from '../model/colorPicker';
import type { AccountDrafts } from '../model/unsavedWork';
import { useSectionRead, type SectionState } from './useSectionRead';

export interface AccountBrandColorApi {
  readonly state: SectionState;
  readonly skeleton: boolean;
  readonly reveal: string;
  readonly onRetry: () => void;

  /** The choice showing right now — the draft if there is one, otherwise what is saved. */
  readonly selection: BrandColor;
  /** Whether the wait belongs on screen. The phone's bars and the ring both fork on this. */
  readonly waiting: boolean;
  readonly paint: BrandPaint;
  readonly onPickPreset: (preset: BrandPresetId) => void;

  /** The picker's own value. It follows `selection` until the trainer touches the picker. */
  readonly hsv: Hsv;
  /** What the HEX field shows: six digits without the `#`, uppercase, possibly half-typed. */
  readonly hexText: string;
  /**
   * The ratio the PICKER's colour would give a client — the mockup's own arrangement: the chip lives
   * inside „Własny kolor" and recomputes from the picked hex even when a tile is pressed.
   */
  readonly customRatio: number;
  /** The colour the picked one is painted in where the label needed it moved, or `null`. */
  readonly adjustedHex: string | null;
  /**
   * The two sample buttons under „Własny kolor": the picker's own colour, uppercase with its `#`,
   * and the label ink a client's button wears. The painted twin is `adjustedHex`.
   */
  readonly pickedHex: string;
  readonly sampleInk: string;
  /**
   * The HEX field holds a half-typed value. It is not a colour, so there is no ratio to report and
   * nothing new to save — the preview stays on the last whole colour.
   */
  readonly hexIncomplete: boolean;
  readonly onPlaneChange: (saturation: number, value: number) => void;
  readonly onHueChange: (hue: number) => void;
  readonly onHexChange: (typed: string) => void;

  readonly isDirty: boolean;
  readonly saving: boolean;
  readonly onSave: () => void;
  readonly saveError: string | null;

  /**
   * The tab's typed-but-unsaved work, for the screen's leave guard (`model/unsavedWork`). Not
   * gated on the read like `isDirty`: off this tab the read is disabled, and a colour picked here
   * is still work to lose from Profil.
   */
  readonly drafts: AccountDrafts['brand'];

  readonly announcement: string;
}

/**
 * What the trainer has changed and not saved: the colour, the plane's own HSV and the text in the
 * HEX field. ONE piece of state because the three are set together and dropped together at four
 * sites — a save, a rollback, a tile and a fresh pick — and three `useState`s made that four places
 * where one of them could be forgotten.
 *
 * Every member is `null` for "follow what is saved", which is what lets the plane seed itself when
 * the read lands and stop following it on the first drag. An effect-seeded draft would be
 * overwritten by the next refetch, mid-drag.
 */
interface BrandDraft {
  readonly color: BrandColor | null;
  readonly hsv: Hsv | null;
  readonly hexText: string | null;
}

const NO_DRAFT: BrandDraft = { color: null, hsv: null, hexText: null };

/**
 * @param active whether Kolor marki is the tab on screen. The read is made only for the tab that
 *   draws it, which is the rule `/raporty` and the Profil tab already follow.
 */
export function useBrandColor(active: boolean): AccountBrandColorApi {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { message: announcement, announce } = useAnnouncer();

  const read = useQuery({ ...brandColorQueryOptions(), enabled: active });
  const section = useSectionRead({ active, reads: [read] });

  const saved = read.data ?? DEFAULT_BRAND_COLOR;
  const [draft, setDraft] = useState<BrandDraft>(NO_DRAFT);
  const selection = draft.color ?? saved;
  const paint = brandPaint(selection);

  const hsv = draft.hsv ?? hexToHsv(paint.solid);
  /** The picker's own colour, derived ONCE: both the field's text and the AA chip read it. */
  const pickerHex = hsvToHex(hsv);
  const pickerPaint = brandPaint({ kind: 'custom', hex: pickerHex });
  const hexText = draft.hexText ?? pickerHex.slice(1).toUpperCase();
  const hexIncomplete = draft.hexText !== null && !isBrandHex(`#${draft.hexText}`);

  const [saveError, setSaveError] = useState<string | null>(null);

  /** One place a picked colour lands, whichever of the three controls picked it. */
  const pickHsv = (next: Hsv) => {
    setDraft({ color: { kind: 'custom', hex: hsvToHex(next) }, hsv: next, hexText: null });
    setSaveError(null);
  };

  const save = useMutation<void, unknown, BrandColor, { previous: BrandColorResponse | undefined }>({
    mutationFn: async (next) => {
      const request = brandColorRequest(next);
      // Both verbs answer with the resulting state; it is discarded and `onSettled` invalidates
      // instead, so one path cannot drift from the other.
      if (request.kind === 'reset') await deleteMeBrandColor();
      else await putMeBrandColor(request.body);
    },
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: brandColorQueryKey });
      const previous = queryClient.getQueryData<BrandColorResponse>(brandColorQueryKey);
      // A dropped row reads back as Flame, so the optimistic shape is the same either way.
      queryClient.setQueryData<BrandColorResponse>(brandColorQueryKey, toBrandColorResponse(next));
      return { previous };
    },
    onSuccess: () => {
      // Dropped HERE, not on dispatch: `onMutate` already wrote the colour into the cache, so
      // nothing repaints. A beat earlier would flash the previous colour for the request's duration.
      setDraft(NO_DRAFT);
      setSaveError(null);
      announce(t('web:account.brandColor.saved'));
    },
    onError: (_error, _next, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(brandColorQueryKey, context.previous);
      }
      // The draft comes back too, so the preview and the tiles agree with the cache again rather
      // than standing on a colour the server refused.
      setDraft(NO_DRAFT);
      setSaveError(t('profile.brandColor.saveFailed'));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: brandColorQueryKey });
    },
  });

  return {
    ...section,

    selection,
    paint,
    onPickPreset: (preset) => {
      // The picker follows the preset's own hue, so opening it after picking a tile starts where
      // the eye is rather than back on red.
      setDraft({ color: { kind: 'preset', preset }, hsv: null, hexText: null });
      setSaveError(null);
    },

    hsv,
    hexText,
    customRatio: pickerPaint.ratio,
    adjustedHex: customFillAdjustment(pickerHex),
    pickedHex: pickerHex.toUpperCase(),
    sampleInk: pickerPaint.ink,
    hexIncomplete,
    onPlaneChange: (saturation, value) => {
      pickHsv({ ...hsv, s: saturation, v: value });
    },
    onHueChange: (hue) => {
      pickHsv({ ...hsv, h: hue });
    },
    onHexChange: (typed) => {
      const digits = normaliseHexInput(typed);
      // A half-typed hex is not a colour: the preview stays on the last committed one until six
      // digits are there, and only then does the plane follow the field.
      if (!isBrandHex(`#${digits}`)) {
        setDraft((current) => ({ ...current, hexText: digits }));
        return;
      }
      const hex = `#${digits.toLowerCase()}`;
      setDraft({ color: { kind: 'custom', hex }, hsv: hsvFromHex(hex, hsv), hexText: digits });
      setSaveError(null);
    },

    isDirty: section.state === 'ready' && !sameBrandColor(selection, saved),
    saving: save.isPending,
    onSave: () => {
      save.mutate(selection);
    },
    saveError,

    drafts: {
      changed: draft.color !== null && !sameBrandColor(selection, saved),
      halfTypedHex: hexIncomplete,
    },

    announcement,
  };
}
